// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC

/**
 * Structured logger — thin adapter over @provide-io/telemetry.
 *
 * Preserves the existing frontend call signature (message-first) so no
 * call-sites need to change. All logs flow through @provide-io/telemetry which
 * provides: PII sanitization, OTEL trace injection, window.__pinoLogs
 * capture, and context binding.
 */

import {
  setupTelemetry,
  getLogger,
  bindContext,
  unbindContext,
  clearContext,
  getContext,
  runWithContext,
  resetTelemetryState,
  resetTraceContext,
  type Logger as TelemetryLogger,
} from '@provide-io/telemetry';

setupTelemetry({
  serviceName: 'undef-frontend',
  environment: import.meta.env.MODE,
  logLevel: import.meta.env.VITE_LOG_LEVEL ?? (import.meta.env.DEV ? 'debug' : 'info'),
  captureToWindow: true,
  consoleOutput: import.meta.env.DEV,
});

// Message-first wrapper — preserves existing call signature across all consumers.
export interface Logger {
  debug(msg: string, context?: Record<string, unknown>): void;
  info(msg: string, context?: Record<string, unknown>): void;
  warn(msg: string, context?: Record<string, unknown>): void;
  error(msg: string, context?: Record<string, unknown>): void;
  child(bindings: Record<string, unknown>): Logger;
}

function _adapt(context?: Record<string, unknown>): Record<string, unknown> {
  return context ? { ...context } : {};
}

function _wrapChild(inner: TelemetryLogger): Logger {
  return {
    debug(msg, ctx) {
      inner.debug(_adapt(ctx), msg);
    },
    info(msg, ctx) {
      inner.info(_adapt(ctx), msg);
    },
    warn(msg, ctx) {
      inner.warn(_adapt(ctx), msg);
    },
    error(msg, ctx) {
      inner.error(_adapt(ctx), msg);
    },
    child(bindings) {
      return _wrapChild(inner.child(bindings));
    },
  };
}

export const logger: Logger = _wrapChild(getLogger('undef-frontend'));

// Re-export telemetry primitives for new code that wants the full API.
export {
  bindContext,
  unbindContext,
  clearContext,
  getContext,
  runWithContext,
  resetTelemetryState,
  resetTraceContext,
};

// Backwards-compatible utility helpers.
export function createChildLogger(context: Record<string, unknown>): Logger {
  return logger.child(context);
}

export function logPerformance(
  name: string,
  duration: number,
  metadata?: Record<string, unknown>
): void {
  logger.info('performance_metric', { metric_name: name, duration_ms: duration, ...metadata });
}

export function logUserAction(action: string, details?: Record<string, unknown>): void {
  logger.info('user_action', { action, ...details });
}

export function logWebSocketEvent(event: string, details?: Record<string, unknown>): void {
  logger.debug('websocket_event', { event, ...details });
}
